import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Shield, ArrowRight, Copy, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = window.location.origin;

export default function ShareScorePage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/assessment/score/${submissionId}`)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [submissionId]);

  const shareUrl = `${SITE_URL}/score/${submissionId}`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    } catch { return ""; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#050B14] flex flex-col items-center justify-center px-6">
        <p className="text-slate-400 mb-6">Assessment not found.</p>
        <button onClick={() => navigate("/")} className="text-sm text-[#D4AF37] hover:text-[#F0CA4E]">
          Return to JCAM
        </button>
      </div>
    );
  }

  const tierColors = {
    "Q-Day Hardened": "#22C55E",
    "Prepared": "#D4AF37",
    "Moderate Risk": "#F59E0B",
    "High Risk": "#EF4444",
    "Critical Risk": "#DC2626",
  };
  const tierColor = tierColors[data.tier] || "#D4AF37";

  return (
    <div className="min-h-screen bg-[#050B14]">
      <div className="noise-overlay" aria-hidden="true" />

      {/* Top bar */}
      <div className="border-b border-slate-800 bg-[#050B14]/90 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <button
            data-testid="share-home-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-3"
          >
            <span className="font-serif text-lg font-bold text-white">JCAM</span>
            <span className="hidden sm:block w-px h-5 bg-slate-700" />
            <span className="hidden sm:block text-xs tracking-[0.2em] uppercase text-slate-400">
              Q-Day Readiness
            </span>
          </button>
          <button
            data-testid="share-take-assessment"
            onClick={() => navigate("/assessment")}
            className="text-xs text-[#D4AF37] hover:text-[#F0CA4E] tracking-wide transition-colors flex items-center gap-1.5"
          >
            Take Assessment <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-6 py-16 md:py-24 text-center">
        {/* Badge */}
        <div className="inline-flex items-center justify-center w-14 h-14 border border-[#D4AF37]/30 mb-6">
          <Shield className="w-6 h-6 text-[#D4AF37]" />
        </div>

        <p className="text-xs tracking-[0.2em] uppercase text-slate-500 mb-2">
          Q-Day Readiness Score
        </p>

        {/* Name / Org */}
        {data.organization && (
          <p data-testid="share-org" className="text-sm text-slate-400 mb-8">
            {data.organization}
          </p>
        )}

        {/* Score circle */}
        <div className="relative w-44 h-44 mx-auto mb-8">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle cx="60" cy="60" r="52" fill="none" stroke="#1E293B" strokeWidth="3" />
            <motion.circle
              cx="60" cy="60" r="52" fill="none"
              stroke={tierColor}
              strokeWidth="3"
              strokeDasharray={2 * Math.PI * 52}
              strokeLinecap="round"
              initial={{ strokeDashoffset: 2 * Math.PI * 52 }}
              animate={{ strokeDashoffset: 2 * Math.PI * 52 * (1 - data.score_percentage / 100) }}
              transition={{ duration: 1.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <motion.span
              className="font-serif text-4xl font-bold text-white"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              {data.score_percentage}
            </motion.span>
            <span className="text-[10px] tracking-[0.15em] uppercase text-slate-500">/ 100</span>
          </div>
        </div>

        {/* Tier */}
        <h1
          data-testid="share-tier"
          className="font-serif text-2xl font-bold tracking-tight mb-2"
          style={{ color: tierColor }}
        >
          {data.tier}
        </h1>

        {data.created_at && (
          <p className="text-xs text-slate-600 mb-10">
            Assessed {formatDate(data.created_at)}
          </p>
        )}

        {/* Share link */}
        <div className="border border-slate-800 bg-[#0A1325]/30 p-4 mb-8">
          <div className="flex items-center gap-3">
            <input
              data-testid="share-url-input"
              type="text"
              readOnly
              value={shareUrl}
              className="flex-1 bg-transparent text-xs text-slate-400 font-mono truncate border-none outline-none"
            />
            <button
              data-testid="share-copy-btn"
              onClick={copyLink}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#D4AF37] transition-colors flex-shrink-0"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>

        {/* CTAs */}
        <div className="flex flex-col gap-3">
          <button
            data-testid="share-cta-assessment"
            onClick={() => navigate("/assessment")}
            className="w-full border border-[#D4AF37] text-[#D4AF37] py-3.5 text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#050B14] transition-all duration-300"
          >
            Take Your Own Assessment
          </button>
          <button
            data-testid="share-cta-home"
            onClick={() => navigate("/")}
            className="w-full border border-slate-800 text-slate-400 py-3.5 text-xs tracking-[0.15em] uppercase hover:border-slate-600 hover:text-white transition-all duration-300"
          >
            Learn About JCAM
          </button>
        </div>

        {/* Footer text */}
        <p className="text-[10px] text-slate-700 mt-10">
          Powered by J. Carter Asset Management's Q-Day Readiness Framework
        </p>
      </div>
    </div>
  );
}
